import { concatStyleSets, IStyleSet, mergeStyles } from '@fluentui/merge-styles';
import { getId, IColumn, MessageBarType } from '@fluentui/react';

import {
  IH2OTheme,
  IMessageBarMessage,
  IStyleFunctionOrObject,
  MessageType,
  SortInfo,
} from '../models/components.models';

/**
 * utility models
 */
type RecursiveKeyOf<T extends object> = { [K in keyof T]: T[K] extends object ? K | RecursiveKeyOf<T[K]> : K }[keyof T];
type SortValue = string | number | Date | boolean | undefined;
type StringType = 'boolean' | 'date' | 'number' | 'string' | 'semver';
export type SortCriterion<T extends object> = [RecursiveKeyOf<T>[], StringType?, boolean?];

/**
 * capitalize: Capitalize the first letter of a string
 * @param str
 * @returns string
 */
export const capitalize = (str: string): string => `${str.charAt(0).toUpperCase()}${str.slice(1)}`;

/**
 * validateEmail: Regex for testing if email string is valid.
 * @param email
 * @returns boolean
 */
export const validateEmail = (email: string): boolean => {
  const re =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
  return re.test(String(email).toLowerCase());
};

/**
 * debounce function
 * @param timeout
 * @param f callback function
 */
export const debounce = (timeout: number, f: (...args: any) => any) => {
  let t: number | null = null;
  return (...args: any) => {
    if (t) window.clearTimeout(t);
    t = window.setTimeout(() => {
      f(...args);
      t = null;
    }, timeout);
  };
};

/**
 * getDate: Returns a Date object from a string
 * @param originalDate
 * @returns Date object
 */
export const getDate = (originalDate = ''): Date | undefined => {
  let date = new Date(originalDate);
  if (originalDate && date.toString() === 'Invalid Date') {
    // format from `YYYY-MM-DD hh:mm:ss.ssss +999 UTC` to `YYYY-MM-DDThh:mm:ss`
    const [_date, _time] = originalDate.toString().split(' '),
      str = [_date, _time.split('.')[0]].join('T');
    date = new Date(str);
  }
  return date.toString() === 'Invalid Date' ? undefined : date;
};

/**
 * Basic helper: Accepts a Date, formats it in the browser's timezone using provided Intl options.
 * @param input Date instance to format
 * @param options Intl.DateTimeFormatOptions
 * @param locale optional locale (defaults to browser locale)
 */
export const formatDateInBrowserTimezone = (
  input: Date | string | number,
  options: Intl.DateTimeFormatOptions,
  locale?: string | string[]
): string => {
  const date = input instanceof Date ? input : new Date(input);
  if (date.toString() === 'Invalid Date') return '';
  return new Intl.DateTimeFormat(locale, options).format(date);
};

// Preset for: "10/23/2025, 03:21:24 PM" (with 'en-US' or similar locale)
export const DATETIME_12H_COMMA: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
};

/**
 * sortList: Pure function that returns a sorted list of objects, based on a key within each object
 * @param items
 * @param key
 * @param descending
 */
export const sortList = <T>(items: T[], key: keyof T, descending = false): T[] =>
  [...items].sort((a: T, b: T) =>
    a[key] === b[key] ? 0 : descending ? (a[key] < b[key] ? 1 : -1) : a[key] > b[key] ? 1 : -1
  );

/**
 * getValueFromKey:
 * @param item
 * @param keys
 */
export const getValueFromKey = (item: { [key: string]: any }, keys: string[]): SortValue =>
  keys.reduce((acc, curr) => (acc ? acc![curr] : item[curr]), undefined);

/**
 * Parses version string according to semver standard into
 * - major.minor.patch which can contain more than 3 segments
 * - pre-release part (after hyphen)
 * - build metadata part (after plus sign)
 * @param version
 */
const parseSemver = (version: string): { buildMetadata: string; preRelease: string; version: string } => {
  const [versionWOBuildMetadata, buildMetadata = ``] = version.split(`+`, 2);
  const [versionWOPreRelease, preRelease = ``] = versionWOBuildMetadata.split(`-`, 2);
  return { buildMetadata, preRelease, version: versionWOPreRelease };
};

/**
 * Implements comparison according to SemVer https://semver.org/#spec-item-11
 * works for integer part (major.minor.patch) as well as for pre-release part,
 * which can contain non-numeric segments. Can handle more than three segments.
 * @param {string} a - major.minor.patch or pre-release part
 * @param {string} b - major.minor.patch or pre-release part
 */
const compareVersions = (a: string, b: string): number => {
  const partsA = a.split(`.`);
  const partsB = b.split(`.`);
  let partA: string,
    partB: string,
    intA: number,
    intB: number,
    difference = 0;
  while (difference === 0 && (partsA.length || partsB.length)) {
    partA = partsA.shift() || ``;
    partB = partsB.shift() || ``;
    intA = Number(partA);
    intB = Number(partB);
    difference = isNaN(intA) || isNaN(intB) ? partA.localeCompare(partB) : intA - intB;
  }
  return difference === 0 ? 0 : difference > 0 ? 1 : -1;
};

/**
 * A comparator for strings that contain a version number - integers delimited
 * by dot. Supports loosely semver specification, the version string can contain
 * pre-release (postfix after `-`) or build metadata (postfix after `+`), but
 * there are violation against the semver:
 * - the normal version number is not limited to 3 segments, it can have less or
 *   more of them (e.g. `1.0` or `4.3.2.1`),
 * - the build metadata are used in comparison (with the least precedence), the
 *   algorithm follows rules for pre-release.
 * @see https://semver.org/#semantic-versioning-200
 * @param {string} a - the first version
 * @param {string} b - the second version
 * @returns {number} either
 * - 0 if arguments are equal
 * - 1 if `a` is greater than `b`
 * - -1 if `b` is greater than `a`
 */
export const compareVersionString = (a: string, b: string): number => {
  if (a === b) return 0;
  const parsedA = parseSemver(a);
  const parsedB = parseSemver(b);
  const difference = compareVersions(parsedA.version, parsedB.version);
  if (difference) return difference;
  if (parsedA.preRelease || parsedB.preRelease) {
    if (!parsedA.preRelease) return 1;
    if (!parsedB.preRelease) return -1;
    const preReleaseDifference = compareVersions(parsedA.preRelease, parsedB.preRelease);
    if (preReleaseDifference) return preReleaseDifference;
  }
  if (parsedA.buildMetadata || parsedB.buildMetadata) {
    if (!parsedA.buildMetadata) return 1;
    if (!parsedB.buildMetadata) return -1;
    return compareVersions(parsedA.buildMetadata, parsedB.buildMetadata);
  }
  return 0;
};

/**
 * Sorting function to sort an array of objects by number, date, boolean, or semver
 * @param keys
 * @param type
 * @param desc
 */
export const sort =
  <T extends object>(keys: RecursiveKeyOf<T>[], type: StringType = 'string', desc = false) =>
  (rawA: T, rawB: T): number => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    let a: SortValue = getValueFromKey(rawA, keys as string[]);
    let b: SortValue = getValueFromKey(rawB, keys as string[]);
    if (a === b) return 0;
    if (a === undefined) return 1;
    if (b === undefined) return -1;
    switch (type) {
      case 'number':
        a = +a;
        b = +b;
        break;
      case 'date':
        a = getDate(String(a)) || new Date();
        b = getDate(String(b)) || new Date();
        break;
      case 'boolean':
        a = Boolean(a);
        b = Boolean(b);
        return desc ? (a ? -1 : 1) : b ? -1 : 1;
      case 'semver': {
        const compareResult = compareVersionString(a as string, b as string);
        return desc ? -compareResult : compareResult;
      }
      default:
        a = String(a).toUpperCase() || '';
        b = String(b).toUpperCase() || '';
        break;
    }
    return desc ? (b > a ? 1 : -1) : b > a ? -1 : 1;
  };

/**
 * sortByMultiple: sortinf function to sort array of objects by multiple fields
 * @param criteria
 */
export const sortByMultiple =
  <T extends object>(criteria: SortCriterion<T>[]) =>
  (rawA: T, rawB: T): number => {
    let index = 0,
      result = sort<T>(...criteria[index])(rawA, rawB);
    while (!result && criteria[index + 1]) {
      index++;
      result = sort<T>(...criteria[index])(rawA, rawB);
    }
    return result;
  };

export const setDetailsListSortColumnAndGetSortInfo = <T extends { [key: string]: any }>(
  col: IColumn,
  columns: IColumn[]
): SortInfo<T> | null => {
  const column = columns.find((c: IColumn) => c.key === col.key);
  if (column) {
    if (!column.isSorted) {
      column.isSorted = true;
      column.isSortedDescending = false;
    } else if (!column.isSortedDescending) {
      column.isSortedDescending = true;
    } else {
      column.isSorted = false;
    }
    return {
      fieldName: col.key as keyof T,
      isSorted: column.isSorted,
      isSortedDescending: column.isSortedDescending,
    };
  }
  return null;
};

export const MessageBarTypeMessageTypeMap = new Map<MessageBarType | undefined, MessageType>([
  [MessageBarType.blocked, MessageType.blocked],
  [MessageBarType.error, MessageType.error],
  [MessageBarType.info, MessageType.info],
  [MessageBarType.severeWarning, MessageType.severe],
  [MessageBarType.success, MessageType.success],
  [MessageBarType.warning, MessageType.warning],
]);

export const getMessageBarMessage = (
  message: string,
  timeout = 0,
  groupId = '',
  messageBarType: MessageBarType = MessageBarType.error
): IMessageBarMessage => {
  const m = { id: getId(), groupId, messageBarType, message, timeout };
  if (groupId) {
    m.groupId = groupId;
  }
  return m;
};

const getStyleObject = <S>(theme: IH2OTheme, functionOrObject: IStyleFunctionOrObject<S>): Partial<S> => {
  return typeof functionOrObject === 'function' ? functionOrObject(theme) : functionOrObject;
};

export const singleOrArrayToArray = <T>(o?: T | T[]) =>
  o !== undefined && o !== null ? (Array.isArray(o) ? o : [o]) : ([] as T[]);

export const getStyles = <S>(
  theme: IH2OTheme,
  defaultStyleFunctionOrObject?: IStyleFunctionOrObject<S>,
  styleFunctionOrObject?: IStyleFunctionOrObject<S> | IStyleFunctionOrObject<S>[]
) => {
  const styles: Partial<S>[] = [];
  if (defaultStyleFunctionOrObject) {
    styles.push(getStyleObject(theme, defaultStyleFunctionOrObject));
  }
  const styleFunctions = singleOrArrayToArray(styleFunctionOrObject);
  styles.push(...styleFunctions.map((s) => getStyleObject(theme, s as IStyleFunctionOrObject<S>)));
  return concatStyleSets(...(styles as unknown as IStyleSet[])) as unknown as Partial<S>;
};

export const getClassNames = <S, C>(
  prefix: string,
  theme: IH2OTheme,
  defaultStyleFunctionOrObject?: IStyleFunctionOrObject<S>,
  styleFunctionOrObject?: IStyleFunctionOrObject<S> | IStyleFunctionOrObject<S>[],
  ...optionalRootClassNames: (string | undefined | boolean)[]
) => {
  const styles = getStyles(theme, defaultStyleFunctionOrObject, styleFunctionOrObject);
  return Object.keys(styles).reduce((classNames: { [key: string]: string }, key) => {
    const classes = [`h2o-${prefix}-${key}`];
    if (key === 'root') {
      const optionClassNames = combineClassNames(...optionalRootClassNames).trim();
      if (optionClassNames) {
        classes.push(optionClassNames);
      }
    }
    const mergeClassName = mergeStyles((styles as any)[key]);
    if (mergeClassName) {
      classes.push(mergeClassName);
    }
    classNames[key] = classes.join(' ');
    return classNames;
  }, {}) as unknown as C;
};

/**
 * Generate root custom class names
 * @param classNames
 * @returns
 */
export const combineClassNames = (...classNames: (string | undefined | boolean)[]): string =>
  classNames.filter(Boolean).join(' ');

/**
 * Get page number by missing index
 * @param missingIndex
 * @param pageSize
 * @returns
 */
export const getPageNumberByIndex = (missingIndex: number, pageSize: number) => Math.floor(missingIndex / pageSize) + 1;
/**
 * start row index by page number
 * @param pageNumber
 * @param pageSize
 * @returns
 */
export const getStartIndexOfPage = (pageNumber: number, pageSize: number) => pageSize * (pageNumber - 1);

export const groupBy = <T, U = string>(arr: T[], key: string): Map<U, T[]> => {
  return arr?.reduce((map: Map<U, T[]>, item: T) => {
    const keyValue = (item as any)[key];
    if (!map.has(keyValue)) map.set(keyValue, []);
    map.get(keyValue)?.push(item);
    return map;
  }, new Map<U, T[]>());
};

export const partition = <T>(arr: T[], predicate: (t: T, index: number) => boolean) => {
  const arr1: T[] = [],
    arr2: T[] = [];
  arr.forEach((item, index) => {
    if (predicate(item, index)) {
      arr1.push(item);
    } else {
      arr2.push(item);
    }
  });
  return [arr1, arr2];
};

export const uniq = <T>(arr: T[]): T[] => Array.from(new Set(arr));

export const uniqBy = <T>(arr: T[], predicate: string): T[] => {
  // Adapted from https://stackoverflow.com/questions/40801349/converting-lodash-uniqby-to-native-javascript
  const cb = (o: T) => (o as any)[predicate];

  return [
    ...arr
      .reduce((map, item) => {
        const key = item === null || item === undefined ? item : cb(item);

        map.has(key) || map.set(key, item);

        return map;
      }, new Map())
      .values(),
  ];
};

export const zipWithLabels = <T, U>(arr1: T[], arr2: U[], label1: string, label2: string): any[] => {
  const maxLen = Math.max(arr1.length, arr2.length);
  return Array.from(Array(maxLen).keys()).map((i) => ({
    [label1]: arr1?.[i],
    [label2]: arr2?.[i],
  }));
};

export const navigate = (href: string, sameTab = false, filename?: string) => {
  if (!href) {
    return;
  }
  const link = document.createElement('a');
  link.href = href;
  if (filename) link.download = filename;
  if (!sameTab) link.target = '_blank';
  link.click();
  link.remove();
};

// taken from https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
export async function sha256(message: string): Promise<string> {
  const utf8Buffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', utf8Buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * eg: bucketName -> Bucket Name
 * @param camelCase
 * @returns
 */
export const camelCaseToTitleCase = (camelCase: string) => {
  const result = camelCase.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

/**
 * This function is to check a value of a field of a form({fieldName: value1, fieldName2: value2, ...}) is changed or not. It can't be used for comparing a general object.
 * This can be used with `Submit` button. <Button type='submit' text='Save' disabled={!hasChangedFieldValues(previousValues, values)} />
 * @param previousValues
 * @param values
 * @returns
 */
export const hasChangedFieldValues = (previousValues: { [key: string]: any }, values: { [key: string]: any }) => {
  // JSON.stringify is for comparing an object value like `[]` === `[]`
  return Object.keys(values).some((key) => JSON.stringify(previousValues[key]) !== JSON.stringify(values[key]));
};

/**
 * Download a text file like txt, csv, json
 * @param filename
 * @param text
 * @param fileExtension txt, csv, json
 */
export const downloadTextFile = (filename: string, text: string, fileExtension = 'txt') => {
  const url = URL.createObjectURL(new Blob([text], { type: `text/${fileExtension};charset=utf-8` }));
  navigate(url, true, `${filename.replace(' ', '_')}.${fileExtension}`);
  URL.revokeObjectURL(url);
};

/**
 * Get the first and last name initials for the chat message
 * @param name
 * @returns
 */
export const getFirstLastInitials = (name: string) => {
  let names = name.split(' ');
  if (names.length === 1) {
    names = name.split(/(?=[A-Z])/);
  }
  return (names.length === 1 ? names[0][0] : `${names[0][0]}${names[names.length - 1][0]}`).toUpperCase();
};

export const relativeTime = (input: Date | string) => {
  const date = input instanceof Date ? input : new Date(input);
  const formatter = new Intl.RelativeTimeFormat('en');
  const ranges = [
    // ['years', 3600 * 24 * 365],
    // ['months', 3600 * 24 * 30],
    // ['weeks', 3600 * 24 * 7],
    ['days', 3600 * 24],
    ['hours', 3600],
    ['minutes', 60],
    ['seconds', 1],
  ] as const;
  const secondsElapsed = (date.getTime() - Date.now()) / 1000;
  for (const [rangeType, rangeVal] of ranges) {
    if (rangeVal < Math.abs(secondsElapsed)) {
      const delta = secondsElapsed / rangeVal;
      return formatter.format(Math.trunc(delta), rangeType);
    }
  }
  return '';
};

export const countChar = (s: string) => {
  const iter = s[Symbol.iterator]();
  let i = 0;
  while (!iter.next().done) i++;
  return i;
};

/**
 * crypto.randomUUID() is available only in secure contexts(https or localhost, 127.0.0.1...). If it is not in secure context, it will use this polyfill
 */
export const randomUUID = () => {
  // old node doesn't support randomUUID, so it uses 'randomUUID' to avoid it.
  if (!(crypto as any)['randomUUID']) {
    // from https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid/2117523#2117523
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c: any) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    );
  } else {
    return (crypto as any)['randomUUID']();
  }
};

type AnyObject = { [key: string]: any };
export const getUniqueOptionsByKeys = <T extends AnyObject>(arr1: T[], arr2: T[], key: keyof T): T[] => {
  const combinedArr = [...arr1, ...arr2];
  const uniqueOptions = new Map<any, T>();

  combinedArr.forEach((option) => {
    if (!uniqueOptions.has(option[key])) {
      uniqueOptions.set(option[key], option);
    }
  });

  return [...uniqueOptions.values()];
};
